import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, CreditCard, TrendingUp, Receipt } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

interface RevenueStatsProps {
  mrr: number;
  activeSubscriptionsCount: number;
  subscriptionsByPlan: { plan: string; count: number; revenue: number }[];
  revenueInPeriod: number;
  paymentsInPeriod: number;
  subscriptionsList: { companyName: string; planType: string; status: string; currentPeriodEnd: string | null }[];
}

export function RevenueStats({
  mrr,
  activeSubscriptionsCount,
  subscriptionsByPlan,
  revenueInPeriod,
  paymentsInPeriod,
  subscriptionsList,
}: RevenueStatsProps) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Receita</h2>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MRR</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(mrr)}</div>
            <p className="text-xs text-muted-foreground">Receita recorrente mensal</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Assinaturas Ativas</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSubscriptionsCount}</div>
            <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
              {subscriptionsByPlan.map((p) => (
                <div key={p.plan}>
                  {p.plan}: {p.count} ({formatCurrency(p.revenue)})
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Avulsa (Período)</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(revenueInPeriod)}</div>
            <p className="text-xs text-muted-foreground">{paymentsInPeriod} pagamento(s) no período</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Total Estimada</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(mrr + revenueInPeriod)}</div>
            <p className="text-xs text-muted-foreground">MRR + avulsa no período</p>
          </CardContent>
        </Card>
      </div>

      {subscriptionsList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Assinaturas Ativas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium">Empresa</th>
                    <th className="text-left py-2 font-medium">Plano</th>
                    <th className="text-left py-2 font-medium">Status</th>
                    <th className="text-left py-2 font-medium">Válido até</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionsList.map((sub, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{sub.companyName}</td>
                      <td className="py-2 capitalize">{sub.planType}</td>
                      <td className="py-2">
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {sub.status}
                        </span>
                      </td>
                      <td className="py-2">
                        {sub.currentPeriodEnd
                          ? format(parseISO(sub.currentPeriodEnd), "dd/MM/yyyy", { locale: ptBR })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
